import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { User } from './useAllUsers';

// The API expects snake_case role values; the UI uses `submanager`.
const ROLE_PARAM_MAP: Record<string, string> = {
    submanager: 'sub_manager',
};

export function toManagerRoleParam(role: string) {
    return ROLE_PARAM_MAP[role] ?? role;
}

// The endpoint has returned the list under a few different keys; accept any of them
// so a rename on the backend surfaces as an error rather than a silently empty list.
// Managers may come back keyed by `id`/`user_id` instead of `_id`. Without a unique id every
// Select item shares `value={undefined}`, so picking one marks all of them selected.
function extractManagers(payload: any): User[] {
    const list = Array.isArray(payload)
        ? payload
        : payload?.managers ?? payload?.data ?? payload?.users;
    if (!Array.isArray(list)) return [];
    return list
        .map((m: any) => ({ ...m, _id: String(m?._id ?? m?.id ?? m?.user_id ?? '') }))
        .filter((m: User) => m._id);
}

/**
 * Fetches the users that can be set as the manager of someone holding `role`,
 * i.e. the next level up:
 *   superadmin  -> []
 *   admin       -> superadmin users
 *   manager     -> admin users
 *   sub_manager -> manager users
 *   member      -> sub_manager users
 *
 * `role` is required by the API (it returns a 422 `detail` payload without it),
 * so the query stays disabled until one is selected.
 */
export function useManagers(role?: string) {
    return useQuery<User[]>({
        queryKey: ['managers', role],
        enabled: Boolean(role),
        queryFn: async () => {
            const response = await api.get('/api/v1/tapanel/get-managers', {
                params: { role: toManagerRoleParam(role!) },
            });
            return extractManagers(response.data);
        },
    });
}
